import { motion } from "framer-motion";
import { Download, Chrome, Shield, CheckCircle, Globe, ArrowRight, Star, Users, Zap, Brain, Smartphone, Monitor, Headphones, Cookie } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ExtensionInstallButton } from "../components/sections/extensionInstallSection";
import { features, stats, testimonials } from "../data";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/navbar";
import { UniversalHero } from "../components/sections/universalHero";

// Import du composant ExtensionInstallButton (assumé être dans un fichier séparé)


// Page principale d'installation de l'extension
export const ExtensionInstallPage: React.FC = () => {
  const { t } = useTranslation();


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-blue-900">
      <Navbar />
      <UniversalHero
        pageName="contact"
        breadcrumbs={[
          { name: "Solution", href: "/" },
          { name: "individual (3wChat extension)", href: "/individual" },
        ]}
        customConfig={{
          title: "Extension 3wChat",
          subtitle: "Votre assistant sur internet",
          description: "Pour vous donner une expérience de navigation unique sur internet",
          primaryColor: "from-black to-indigo-500",
          secondaryColor: "bg-gradient-to-r from-purple-600 to-pink-600",
          icon: Cookie, // Assure-toi que l'icône Contact est bien importée
          badge: "Support & Assistance",
          backgroundPattern: "gradient",
          cta: {
            text: "Installer maintenant",
            link: "#individuel", // ancre vers le formulaire
            // secondary: {
            //   text: "Nos bureaux",
            //   link: "/contact#localisation"
            // }
          }
        }}
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-8"
            >
              <div className="inline-flex items-center space-x-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                <span>{t('extensionPage.hero.badge')}</span>
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6">
                {t('extensionPage.hero.title')}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                  3wChat
                </span>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto mb-10">
                {t('extensionPage.hero.subtitle')}
              </p>
            </motion.div>

            {/* Section d'installation principale */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white dark:bg-gray-900 rounded-3xl p-8 shadow-2xl max-w-2xl mx-auto mb-16"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {t('extensionPage.instructions.title')}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t('extensionPage.instructions.description')}
                </p>
              </div>

              <ExtensionInstallButton />

              <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-500 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>{t('extensionPage.secure')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Download className="w-4 h-4 text-blue-500" />
                  <span>{t('extensionPage.free')}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4 text-purple-500" />
                  <span>{t('extensionPage.verified')}</span>
                </div>
              </div>

              {/* Instructions d'installation */}
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-xl"
              >
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                  <Monitor className="w-5 h-5 mr-2 text-blue-500" />
                  {t('extensionPage.instructions.title')}
                </h4>
                <ol className="text-left text-sm text-gray-600 dark:text-gray-300 space-y-3">
                  <li className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    <span>{t('extensionPage.instructions.step1')}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    <span>{t('extensionPage.instructions.step2')}</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    <span>{t('extensionPage.instructions.step3')}</span>
                  </li>
                </ol>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section Statistiques */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl lg:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 mb-2">
                  {t(stat.number)}
                </div>
                <div className="text-gray-600 dark:text-gray-300 font-medium">
                  {t(stat.label)}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Fonctionnalités */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('extensionPage.features.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('extensionPage.features.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
                className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {t(feature.title)}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {t(feature.description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Témoignages */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {t('extensionPage.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('extensionPage.testimonials.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6 italic">
                  "{t(testimonial.content)}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t(testimonial.name)}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t(testimonial.role)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('extensionPage.cta.title')}
            </h2>
            <p className="text-xl mb-10 opacity-90">
              {t('extensionPage.cta.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <ExtensionInstallButton />

              <motion.a
                href="#features"
                whileHover={{ scale: 1.05 }}
                className="inline-flex items-center space-x-2 text-white/90 hover:text-white transition-colors"
              >
                <span>{t('extensionPage.cta.learnMore')}</span>
                <ArrowRight className="w-4 h-4" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
};